import { interpolate, type TranslateParams } from "./interpolate";

export type { TranslateParams } from "./interpolate";

export const DEFAULT_LOCALE = "de-DE";

export interface TranslationBackend {
  /**
   * Resolve `key`, falling back to `defaultText` whenever the key is unknown.
   * Implementations must interpolate `{{param}}` placeholders.
   */
  translate: (
    key: string,
    defaultText: string,
    params?: TranslateParams,
  ) => string;
  /** Active BCP-47 locale, e.g. "de-DE". */
  getLocale: () => string;
  setLocale: (locale: string) => void | Promise<void>;
  /** Locales offered to the user. A length <= 1 means "do not show a switcher". */
  getAvailableLocales: () => readonly string[];
  /** Notify on locale/resource changes. Returns an unsubscribe function. */
  subscribe: (listener: () => void) => () => void;
}

/**
 * Pass-through backend used when no i18n module is installed: every lookup
 * returns its inline default text, so projects without i18n keep their current
 * behaviour without any extra dependency or configuration.
 */
function createDefaultBackend(): TranslationBackend {
  let locale = DEFAULT_LOCALE;
  const listeners = new Set<() => void>();

  return {
    translate: (_key, defaultText, params) => interpolate(defaultText, params),
    getLocale: () => locale,
    setLocale: (next) => {
      if (next === locale) {
        return;
      }
      locale = next;
      for (const listener of listeners) {
        listener();
      }
    },
    getAvailableLocales: () => [locale],
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

let globalBackend: TranslationBackend = createDefaultBackend();

/**
 * Bumped whenever the active backend changes or the active backend reports a
 * locale/resource change. Serves as the `useSyncExternalStore` snapshot.
 */
let version = 0;
const changeListeners = new Set<() => void>();
let unsubscribeFromBackend: (() => void) | undefined;

function emitChange(): void {
  version += 1;
  for (const listener of changeListeners) {
    listener();
  }
}

function attachToBackend(): void {
  unsubscribeFromBackend?.();
  unsubscribeFromBackend =
    changeListeners.size > 0 ? globalBackend.subscribe(emitChange) : undefined;
}

/**
 * Subscribes to translation changes across backend swaps, so components stay
 * live even when the i18n module registers itself after the first render.
 */
export function subscribeToTranslationChanges(
  listener: () => void,
): () => void {
  changeListeners.add(listener);
  if (!unsubscribeFromBackend) {
    attachToBackend();
  }
  return () => {
    changeListeners.delete(listener);
    if (changeListeners.size === 0) {
      unsubscribeFromBackend?.();
      unsubscribeFromBackend = undefined;
    }
  };
}

export function getTranslationVersion(): number {
  return version;
}

export function registerTranslationBackend(backend: TranslationBackend): void {
  globalBackend = backend;
  attachToBackend();
  emitChange();
}

export function getTranslationBackend(): TranslationBackend {
  return globalBackend;
}

/**
 * Restores the pass-through backend. Primarily useful in tests.
 */
export function resetTranslationBackend(): void {
  registerTranslationBackend(createDefaultBackend());
}

/**
 * Non-reactive translation for code outside the React tree (notifier messages,
 * error formatting, plain helper functions). Components should use
 * `useTranslation` so they re-render on locale changes.
 */
export function translate(
  key: string,
  defaultText: string,
  params?: TranslateParams,
): string {
  return globalBackend.translate(key, defaultText, params);
}

export function getLocale(): string {
  return globalBackend.getLocale();
}
